import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { format, parseISO } from "date-fns";
import { CheckCircle2, XCircle, Clock, Award, UserPlus } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { CoachStageStepper, CoachStageSetter, ProposalComments, ProposalHistory } from "@/components/proposals/CoachStages";

const STATUS_TABS = [
  { key: "pending",  label: "Pending"  },
  { key: "approved", label: "Approved" },
  { key: "rejected", label: "Rejected" },
];

function fmtDate(d) {
  try { return format(parseISO(d), "d MMM yyyy · HH:mm"); } catch { return d; }
}

export function WelcomeTemplateEditor() {
  const [tpl, setTpl] = useState(null);
  const [draft, setDraft] = useState("");
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    api.get("/admin/coach-welcome-template").then(r => { setTpl(r.data); setDraft(r.data.body); }).catch(() => {});
  }, []);
  if (!tpl) return null;
  const dirty = draft.trim() !== tpl.body.trim();
  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/admin/coach-welcome-template", { body: draft });
      setTpl(data); setDraft(data.body); toast.success("Welcome email wording saved");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Couldn't save"); }
    finally { setSaving(false); }
  };
  const reset = async () => {
    setSaving(true);
    try {
      const { data } = await api.post("/admin/coach-welcome-template/reset");
      setTpl(data); setDraft(data.body); toast.success("Reset to default wording");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Couldn't reset"); }
    finally { setSaving(false); }
  };
  return (
    <div className="rounded-md border border-border p-3 space-y-2" data-testid="coach-welcome-template-card">
      <div className="flex items-center justify-between gap-3">
        <div>
          <div className="text-[10px] tracking-widest uppercase opacity-70">Welcome email body</div>
          <div className="text-[11px] opacity-60">Subject: <span className="opacity-100 font-semibold">{tpl.subject}</span> · sign-in details are appended automatically</div>
        </div>
        {!tpl.is_default && (
          <button type="button" onClick={reset} disabled={saving} data-testid="coach-welcome-template-reset" className="text-[10px] tracking-widest uppercase opacity-70 hover:opacity-100 underline">Reset to default</button>
        )}
      </div>
      <Textarea rows={6} value={draft} onChange={e => setDraft(e.target.value)} data-testid="coach-welcome-template-body" className="text-xs" />
      {dirty && (
        <button type="button" onClick={save} disabled={saving || !draft.trim()} data-testid="coach-welcome-template-save" className="btn-volt px-4 py-2 rounded text-[10px] tracking-widest uppercase disabled:opacity-40">
          {saving ? "Saving…" : "Save wording"}
        </button>
      )}
    </div>
  );
}

export function ProposalsTab() {
  const [status, setStatus] = useState("pending");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [decideOn, setDecideOn] = useState(null); // {proposal, action: "approve"|"reject"}
  const [decideNotes, setDecideNotes] = useState("");
  const [sendWelcome, setSendWelcome] = useState(true);
  const [busy, setBusy] = useState(false);

  const load = async (s = status) => {
    setLoading(true);
    try {
      const { data } = await api.get(`/proposals?status=${s}`);
      setRows(data);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Couldn't load proposals");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(status); /* eslint-disable-next-line */ }, [status]);

  const openDecide = (proposal, action) => {
    setDecideOn({ proposal, action });
    setDecideNotes("");
    setSendWelcome(true);
  };

  const decide = async () => {
    if (!decideOn) return;
    if (decideOn.action === "reject" && !decideNotes.trim()) {
      toast.error("Please add a reason so the proposer knows why");
      return;
    }
    setBusy(true);
    try {
      const body = { notes: decideNotes.trim() || null };
      if (decideOn.action === "approve" && decideOn.proposal.kind === "coach") {
        body.send_welcome_email = sendWelcome;
      }
      const { data } = await api.post(`/proposals/${decideOn.proposal.id}/${decideOn.action}`, body);
      if (decideOn.action === "approve") {
        if (decideOn.proposal.kind === "coach" && data.proposal?.temp_password) {
          toast.success(`Coach created · temp password ${data.proposal.temp_password}${data.proposal.welcome_email_sent ? " · welcome sent" : ""}${data.proposal.proposer_cc_sent ? " · proposer cc'd" : ""}`, { duration: 15000 });
        } else {
          toast.success("Approved");
          window.dispatchEvent(new Event("admin-queue-changed"));
        }
      } else {
        toast.success("Rejected · proposer has been notified");
        window.dispatchEvent(new Event("admin-queue-changed"));
      }
      setDecideOn(null);
      load(status);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || `Couldn't ${decideOn.action}`);
    } finally { setBusy(false); }
  };

  const renderPayload = (p) => {
    if (p.kind === "referee_change") {
      const x = p.payload; const cur = x.current || {};
      const Row = ({ label, k }) => x[k] ? <div><span className="opacity-60">{label}:</span> <span className="line-through opacity-50">{cur[k] || "—"}</span> → <span className="text-[#83B68B] font-medium">{x[k]}</span></div> : null;
      return (<><Row label="Phone" k="phone" /><Row label="Email" k="email" /><Row label="Level" k="level" /></>);
    }
    if (p.kind === "referee") {
      const x = p.payload;
      return (
        <>
          <div><span className="opacity-60">Level:</span> {x.level || "—"}</div>
          <div><span className="opacity-60">Phone:</span> {x.phone || "—"}</div>
          <div><span className="opacity-60">Email:</span> {x.email || "—"}</div>
          <div><span className="opacity-60">Up to:</span> {x.max_age_group || "Any"} · {x.is_qualified ? "Qualified" : "Unqualified"}</div>
        </>
      );
    }
    const x = p.payload;
    return (
      <>
        <div><span className="opacity-60">Email:</span> {x.email}</div>
        <div><span className="opacity-60">Phone:</span> {x.phone || "—"}</div>
        <div><span className="opacity-60">FAN:</span> {x.fan_number || "—"}</div>
        <div><span className="opacity-60">Date of birth:</span> {x.date_of_birth || "—"}</div>
        <div><span className="opacity-60">Age group:</span> {x.age_group || "—"}</div>
        {x.prior_experience && <div className="whitespace-pre-wrap"><span className="opacity-60">Experience:</span> {x.prior_experience}</div>}
        {(x.dbs_held || x.safeguarding_children_held || x.intro_first_aid_expiry || x.highest_coaching_qualification) && (
          <div className="opacity-80"><span className="opacity-60">Compliance:</span> {x.dbs_held ? `DBS ✓${x.dbs_expiry ? ` (to ${x.dbs_expiry})` : ""}` : "DBS —"} · {x.safeguarding_children_held ? `Safeguarding ✓${x.safeguarding_children_expiry ? ` (to ${x.safeguarding_children_expiry})` : ""}` : "Safeguarding —"}{x.intro_first_aid_expiry ? ` · First aid to ${x.intro_first_aid_expiry}` : ""}{x.highest_coaching_qualification ? ` · ${x.highest_coaching_qualification}` : ""}</div>
        )}
      </>
    );
  };

  const displayName = (p) => (p.kind === "referee" || p.kind === "referee_change")
    ? p.payload.name
    : `${p.payload.first_name} ${p.payload.last_name}`.trim();

  return (
    <div className="space-y-6">
      <div>
        <div className="text-[10px] tracking-[0.3em] uppercase text-[#83B68B]">Approvals</div>
        <h2 className="font-display font-black text-3xl mt-1">Proposals</h2>
        <p className="opacity-60 text-sm mt-1">New referees + coaches proposed by coaches / members. Approving materialises the record in the club directory.</p>
      </div>
      <div className="flex gap-2 flex-wrap" data-testid="proposals-status-tabs">
        {STATUS_TABS.map(s => (
          <button
            key={s.key}
            onClick={() => setStatus(s.key)}
            data-testid={`proposals-status-${s.key}`}
            className={`px-4 py-2 rounded text-[10px] tracking-widest uppercase border transition-colors ${status === s.key ? "border-[#83B68B] bg-[#83B68B]/15 text-[#83B68B]" : "border-border hover:border-white/40"}`}
          >
            {s.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="opacity-50 font-display tracking-widest">LOADING…</div>
      ) : rows.length === 0 ? (
        <div className="card-tactical rounded-md p-10 text-center opacity-60" data-testid="proposals-empty">
          No {status} proposals right now.
        </div>
      ) : (
        <div className="space-y-3" data-testid="proposals-list">
          {rows.map(p => (
            <div key={p.id} className="card-tactical rounded-md p-5" data-testid={`proposal-row-${p.id}`}>
              <div className="flex flex-wrap gap-4 items-start justify-between">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    {(p.kind === "referee" || p.kind === "referee_change")
                      ? <Award className="w-4 h-4 text-[#83B68B]" />
                      : <UserPlus className="w-4 h-4 text-[#83B68B]" />}
                    <span className="text-[9px] tracking-widest uppercase opacity-60">{p.kind === "referee_change" ? "referee details change" : p.kind}</span>
                    <div className="font-display font-bold text-lg">{displayName(p)}</div>
                    {p.status === "pending"  && <span className="text-[9px] tracking-widest uppercase px-1.5 py-0.5 rounded border border-amber-400/40 text-amber-300 bg-amber-500/10 inline-flex items-center gap-1"><Clock className="w-3 h-3" /> Pending</span>}
                    {p.status === "approved" && <span className="text-[9px] tracking-widest uppercase px-1.5 py-0.5 rounded border border-[#83B68B]/40 text-[#83B68B] bg-[#83B68B]/10 inline-flex items-center gap-1"><CheckCircle2 className="w-3 h-3" /> Approved</span>}
                    {p.status === "rejected" && <span className="text-[9px] tracking-widest uppercase px-1.5 py-0.5 rounded border border-red-400/40 text-red-300 bg-red-500/10 inline-flex items-center gap-1"><XCircle className="w-3 h-3" /> Rejected</span>}
                  </div>
                  <div className="mt-2 text-xs space-y-0.5 opacity-90">{renderPayload(p)}</div>
                  {p.kind === "coach" && (
                    <div className="mt-3 space-y-2">
                      <CoachStageStepper stage={p.stage} status={p.status} />
                      {p.status === "pending" && <CoachStageSetter p={p} onChanged={np => setRows(rs => rs.map(x => x.id === np.id ? np : x))} />}
                      <ProposalHistory p={p} />
                      <ProposalComments p={p} compact onChanged={np => setRows(rs => rs.map(x => x.id === np.id ? np : x))} />
                    </div>
                  )}
                  {p.payload?.notes && (
                    <div className="mt-3 border-l-2 border-[#83B68B]/40 pl-3 text-xs opacity-80 italic whitespace-pre-wrap" data-testid={`proposal-notes-${p.id}`}>
                      {p.payload.notes}
                    </div>
                  )}
                  <div className="mt-3 text-[10px] opacity-50 tracking-widest uppercase">
                    Submitted {fmtDate(p.created_at)} by {p.proposer_name}
                    {p.decided_at && <> · Decided {fmtDate(p.decided_at)} by {p.decided_by_name}</>}
                  </div>
                  {p.decision_notes && (
                    <div className="mt-2 text-[11px] opacity-80" data-testid={`proposal-decision-${p.id}`}>
                      <span className="opacity-60">Admin note:</span> {p.decision_notes}
                    </div>
                  )}
                </div>
                {p.status === "pending" && (
                  <div className="flex gap-2 flex-shrink-0">
                    <button
                      onClick={() => openDecide(p, "reject")}
                      data-testid={`proposal-reject-${p.id}`}
                      className="px-4 py-2 rounded border border-red-400/60 text-red-300 hover:bg-red-500/10 text-[10px] tracking-widest uppercase inline-flex items-center gap-1.5"
                    >
                      <XCircle className="w-3.5 h-3.5" /> Reject
                    </button>
                    <button
                      onClick={() => openDecide(p, "approve")}
                      data-testid={`proposal-approve-${p.id}`}
                      className="btn-volt px-4 py-2 rounded text-[10px] tracking-widest uppercase inline-flex items-center gap-1.5"
                    >
                      <CheckCircle2 className="w-3.5 h-3.5" /> Approve
                    </button>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={!!decideOn} onOpenChange={(v) => { if (!v) setDecideOn(null); }}>
        <DialogContent className="max-w-md" data-testid="proposal-decide-dialog">
          <DialogHeader>
            <DialogTitle className="font-display tracking-tight text-2xl">
              {decideOn?.action === "approve" ? "Approve proposal" : "Reject proposal"}
            </DialogTitle>
            <DialogDescription className="text-xs opacity-70">
              {decideOn?.action === "approve"
                ? decideOn?.proposal?.kind === "coach"
                  ? "A coach account will be created and (optionally) a welcome email sent. The proposer will be notified."
                  : "The referee will be added to the directory. The proposer will be notified."
                : "Please add a reason — this goes to the proposer so they understand the decision."}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="text-[10px] tracking-widest uppercase opacity-70">
                {decideOn?.action === "approve" ? "Admin note (optional)" : "Reason"}
              </label>
              <Textarea
                rows={3}
                value={decideNotes}
                onChange={e => setDecideNotes(e.target.value)}
                placeholder={decideOn?.action === "approve" ? "Anything you want the proposer to see…" : "Why aren't we going with this proposal?"}
                data-testid="proposal-decision-notes"
              />
            </div>
            {decideOn?.action === "approve" && decideOn?.proposal?.kind === "coach" && (
              <div className="flex items-center gap-3 rounded-md border border-border px-3 py-2">
                <Switch checked={sendWelcome} onCheckedChange={setSendWelcome} data-testid="proposal-send-welcome" />
                <div>
                  <div className="text-sm font-semibold">Send welcome email</div>
                  <div className="text-[11px] opacity-60">Sends "Welcome to Elsenham Youth Football Club" with their temp password + login link, and copies the proposer (without the password). Edit the wording below.</div>
                </div>
              </div>
            )}
            {decideOn?.action === "approve" && decideOn?.proposal?.kind === "coach" && sendWelcome && <WelcomeTemplateEditor />}
            <div className="flex gap-2 pt-1">
              <button
                onClick={() => setDecideOn(null)}
                className="flex-1 py-3 rounded-md border border-border text-xs tracking-widest uppercase font-display font-bold hover:border-white/40"
                data-testid="proposal-decide-cancel"
              >Cancel</button>
              <button
                onClick={decide}
                disabled={busy}
                data-testid="proposal-decide-confirm"
                className={`flex-1 py-3 rounded-md text-xs tracking-widest uppercase font-display font-bold disabled:opacity-40 ${decideOn?.action === "approve" ? "btn-volt" : "border border-red-400/60 text-red-300 hover:bg-red-500/10"}`}
              >
                {busy ? "Working…" : decideOn?.action === "approve" ? "Approve" : "Reject"}
              </button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
